import axios from 'axios';
import {
  DashboardAreaOption,
  DashboardMapData,
  DashboardOverview,
  DashboardTrends,
  DemoVoteAutomationStatus,
  QotdCalendarItem,
} from '../types/dashboard';

const API_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api\/v1\/?$/, '')
  : window.location.hostname === 'localhost'
    ? 'http://localhost:8002'
    : 'https://hwa-nyc-api.fly.dev';

const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: false,
});

const DASHBOARD_PATH = '/api/v1/dashboard';
const QOTD_PATH = '/api/v1/qotd';

const toIso = (value: Date | string) => (value instanceof Date ? value.toISOString() : value);

const dashboardApi = {
  getOverview: async (params: {
    startAt: Date | string;
    endAt: Date | string;
    favoriteNeighborhoodId?: string;
  }): Promise<DashboardOverview> => {
    const response = await axiosInstance.get(`${DASHBOARD_PATH}/overview`, {
      params: {
        start_at: toIso(params.startAt),
        end_at: toIso(params.endAt),
        favorite_neighborhood_id: params.favoriteNeighborhoodId,
      },
    });
    return response.data;
  },

  getTrends: async (params: {
    startAt: Date | string;
    endAt: Date | string;
    level: 'city' | 'borough' | 'neighborhood' | 'block_group';
    areaId?: string;
    includePrevious?: boolean;
  }): Promise<DashboardTrends> => {
    const response = await axiosInstance.get(`${DASHBOARD_PATH}/trends`, {
      params: {
        start_at: toIso(params.startAt),
        end_at: toIso(params.endAt),
        level: params.level,
        area_id: params.areaId,
        include_previous: params.includePrevious ?? false,
      },
    });
    return response.data;
  },

  getAreas: async (
    level: 'city' | 'borough' | 'neighborhood' | 'block_group',
    search?: string
  ): Promise<DashboardAreaOption[]> => {
    const response = await axiosInstance.get(`${DASHBOARD_PATH}/areas`, {
      params: { level, search: search || undefined },
    });
    return response.data;
  },

  getMapData: async (params: {
    startAt: Date | string;
    endAt: Date | string;
    zoom: number;
    layer: 'sentiment' | 'density';
    bounds: { minLng: number; minLat: number; maxLng: number; maxLat: number };
  }): Promise<DashboardMapData> => {
    const response = await axiosInstance.get(`${DASHBOARD_PATH}/map`, {
      params: {
        start_at: toIso(params.startAt),
        end_at: toIso(params.endAt),
        zoom: params.zoom,
        layer: params.layer,
        min_lng: params.bounds.minLng,
        min_lat: params.bounds.minLat,
        max_lng: params.bounds.maxLng,
        max_lat: params.bounds.maxLat,
      },
    });
    return response.data;
  },

  getDemoVoteStatus: async (): Promise<DemoVoteAutomationStatus> => {
    const response = await axiosInstance.get(`${DASHBOARD_PATH}/demo-votes/status`);
    return response.data;
  },

  setDemoVoteEnabled: async (enabled: boolean): Promise<DemoVoteAutomationStatus> => {
    const response = await axiosInstance.put(`${DASHBOARD_PATH}/demo-votes/settings`, { enabled });
    return response.data;
  },

  runDemoVotesNow: async (): Promise<DemoVoteAutomationStatus> => {
    const response = await axiosInstance.post(`${DASHBOARD_PATH}/demo-votes/run`);
    return response.data;
  },

  getQotdCalendar: async (params: { year: number; month: number }): Promise<{ items: QotdCalendarItem[] }> => {
    const response = await axiosInstance.get(`${QOTD_PATH}/calendar`, {
      params,
    });
    return response.data;
  },

  saveQotdQuestion: async (
    date: string,
    payload: { question_text: string; option_negative_text: string; option_positive_text: string }
  ): Promise<{ success: boolean; question_id: number }> => {
    const response = await axiosInstance.put(`${QOTD_PATH}/question`, payload, {
      params: { date },
    });
    return response.data;
  },

  deleteQotdQuestion: async (date: string): Promise<{ success: boolean }> => {
    const response = await axiosInstance.delete(`${QOTD_PATH}/question`, { params: { date } });
    return response.data;
  },

  getQotdResults: async (date: string, languageCode = 'en') => {
    const response = await axiosInstance.get(`${QOTD_PATH}/results`, {
      params: { date, language_code: languageCode },
    });
    return response.data;
  },

  getQotdMap: async (params: {
    date: string;
    zoom: number;
    bounds: { minLng: number; minLat: number; maxLng: number; maxLat: number };
  }) => {
    const response = await axiosInstance.get(`${QOTD_PATH}/map`, {
      params: {
        date: params.date,
        zoom: params.zoom,
        min_lng: params.bounds.minLng,
        min_lat: params.bounds.minLat,
        max_lng: params.bounds.maxLng,
        max_lat: params.bounds.maxLat,
      },
    });
    return response.data;
  },
};

export default dashboardApi;
